import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { ArrowLeft, ShieldCheck, Eye, EyeOff } from "lucide-react";

const SPECIAL = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

function checkStrength(pw) {
  return {
    length: pw.length >= 12,
    digit: /\d/.test(pw),
    special: SPECIAL.test(pw),
    upper: /[A-Z]/.test(pw),
  };
}

export default function AdminProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const strength = checkStrength(newPwd);
  const allValid = strength.length && strength.digit && strength.special && newPwd === confirmPwd && oldPwd && newPwd !== oldPwd;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!strength.length) return setError("Le mot de passe doit contenir au moins 12 caractères.");
    if (!strength.digit) return setError("Le mot de passe doit contenir au moins un chiffre.");
    if (!strength.special) return setError("Le mot de passe doit contenir au moins un caractère spécial.");
    if (newPwd !== confirmPwd) return setError("Les deux mots de passe ne correspondent pas.");
    if (newPwd === oldPwd) return setError("Le nouveau mot de passe doit être différent de l'ancien.");

    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      setSuccess("Mot de passe mis à jour avec succès. Tu restes connecté·e.");
      setOldPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec du changement de mot de passe");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return <div className="py-32 text-center font-mono-label">CHARGEMENT…</div>;

  return (
    <div className="min-h-screen bg-[#FAF8F3]" data-testid="admin-profile">
      <header className="border-b-2 border-[#1A1A18] bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" data-testid="profile-back-dashboard" className="flex items-center gap-2 text-[#1A1A18] hover:text-[#4A7A3D]">
              <ArrowLeft size={16} />
              <Logo size="sm" />
            </Link>
            <span className="font-mono-label text-[#4A7A3D] hidden md:inline">CMS · MON COMPTE</span>
          </div>
          <button onClick={() => navigate("/admin")} className="font-mono-label text-[#1A1A18] hover:text-[#4A7A3D]" data-testid="profile-back-cta">
            Retour →
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="font-mono-label text-[#4A7A3D] mb-3 flex items-center gap-2">
          <ShieldCheck size={14} /> Sécurité du compte
        </div>
        <h1 className="font-display text-4xl md:text-5xl mb-3">Changer le mot de passe</h1>
        <p className="text-[#4B4B48] leading-relaxed mb-10">
          Connecté en tant que <strong className="text-[#1A1A18]">{user.email}</strong>. Choisis un mot de passe robuste — il protège l'accès à toute la rédaction.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6 border-2 border-[#1A1A18] p-6 md:p-8 bg-[#FAF8F3]">
          <div>
            <label htmlFor="old-password" className="font-mono-label text-[#1A1A18] block mb-2">Mot de passe actuel</label>
            <input
              id="old-password"
              type={show ? "text" : "password"}
              value={oldPwd}
              onChange={(e) => setOldPwd(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-4 py-3 font-body focus:outline-none focus:border-[#4A7A3D]"
              data-testid="profile-old-password-input"
            />
          </div>

          <div>
            <label htmlFor="new-password" className="font-mono-label text-[#1A1A18] block mb-2">Nouveau mot de passe</label>
            <input
              id="new-password"
              type={show ? "text" : "password"}
              value={newPwd}
              onChange={(e) => setNewPwd(e.target.value)}
              required
              minLength={12}
              autoComplete="new-password"
              className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-4 py-3 font-body focus:outline-none focus:border-[#4A7A3D]"
              data-testid="profile-new-password-input"
            />
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
              <li className={strength.length ? "text-[#4A7A3D]" : "text-[#4B4B48]"} data-testid="rule-length">
                {strength.length ? "✓" : "○"} Au moins 12 caractères
              </li>
              <li className={strength.digit ? "text-[#4A7A3D]" : "text-[#4B4B48]"} data-testid="rule-digit">
                {strength.digit ? "✓" : "○"} Au moins un chiffre
              </li>
              <li className={strength.special ? "text-[#4A7A3D]" : "text-[#4B4B48]"} data-testid="rule-special">
                {strength.special ? "✓" : "○"} Au moins un caractère spécial
              </li>
              <li className={strength.upper ? "text-[#4A7A3D]" : "text-[#4B4B48]"} data-testid="rule-upper">
                {strength.upper ? "✓" : "○"} (Conseillé) une majuscule
              </li>
            </ul>
          </div>

          <div>
            <label htmlFor="confirm-password" className="font-mono-label text-[#1A1A18] block mb-2">Confirmer le nouveau mot de passe</label>
            <input
              id="confirm-password"
              type={show ? "text" : "password"}
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              required
              autoComplete="new-password"
              className="w-full border-2 border-[#1A1A18] bg-[#FAF8F3] px-4 py-3 font-body focus:outline-none focus:border-[#4A7A3D]"
              data-testid="profile-confirm-password-input"
            />
            {confirmPwd && newPwd !== confirmPwd && (
              <p className="mt-2 text-xs text-red-700" data-testid="confirm-mismatch">Les mots de passe ne correspondent pas.</p>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="font-mono-label text-[#4A7A3D] hover:underline flex items-center gap-2"
            data-testid="profile-toggle-visibility"
          >
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
            {show ? "Masquer les mots de passe" : "Afficher les mots de passe"}
          </button>

          {error && (
            <div className="border-l-4 border-red-700 bg-red-50 p-4 text-sm text-red-900" data-testid="profile-error">
              {error}
            </div>
          )}
          {success && (
            <div className="border-l-4 border-[#4A7A3D] bg-[#4A7A3D]/10 p-4 text-sm text-[#1A1A18]" data-testid="profile-success">
              {success}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              disabled={!allValid || submitting}
              className="bg-[#4A7A3D] text-[#FAF8F3] px-6 py-3 font-mono-label hover:bg-[#3E6633] disabled:bg-[#E0D8CE] disabled:text-[#4B4B48] disabled:cursor-not-allowed"
              data-testid="profile-submit"
            >
              {submitting ? "Mise à jour…" : "Mettre à jour le mot de passe"}
            </button>
            <Link to="/admin" className="border-2 border-[#1A1A18] px-6 py-3 font-mono-label text-center hover:bg-[#F5EFEB]" data-testid="profile-cancel">
              Annuler
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
